import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { AlertTriangle, Ban, Mail, Pause, Play, Send, Unlock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { useWpwMailLetterMutations, useWpwMailLetters } from "../hooks";
import { fmtDate, fmtDateTime } from "../utils";
import type { Parcel, ParcelOwner, WpwMailBulkAction, WpwMailLetter, WpwMailState } from "../types";

/**
 * Ścieżka listowna WPW na karcie działki (sekcja „Podstawa dostępu” → „Listy do właścicieli”):
 * lista listów automatycznych czekających na wysyłkę i już wysłanych, z terminem, powodem
 * wstrzymania, zleceniem wydruku i rozstrzygnięciem zwrotu. Na liście jest tylko jednorazowe
 * „Anuluj wysyłkę” (nowe zlecenie WPW uzbraja list ponownie); blokady TRWAŁE żyją gdzie
 * indziej — cała działka w „Danych ogólnych” (ParcelMailBlockControl), właściciel na jego
 * karcie (OwnerMailBlockControl). Nic z tego nie widzi podwykonawca.
 *
 * Po wysyłce kompilacja składa list KOLEJNY (na wypadek odmowy i ponownej wysyłki) — wychodzi
 * tylko ręcznie i dopiero, gdy poprzedni ma wynik. Każdy wysłany list bez wyniku koordynator
 * może „Oznaczyć jako nieaktualny” — wtedy liczy się tak, jakby nie został wysłany (odpięty od
 * właściciela, bez odmowy; nowe zamówienie WPW uruchamia automat). List pamięta trasę z chwili
 * wysyłki — gdy kolejna wgrana trasa zmieni przebieg na działce, obok jest komunikat „może być
 * nieaktualny” z powodem.
 */

const STATE_TONE: Record<WpwMailState, string> = {
  none: "text-[var(--color-text-muted)]",
  ready: "text-[var(--color-text-muted)]",
  scheduled: "text-blue-700 dark:text-blue-400",
  held: "text-amber-700 dark:text-amber-400",
  blocked: "text-amber-700 dark:text-amber-400",
  dispatched: "text-blue-700 dark:text-blue-400",
  sent: "text-emerald-700 dark:text-emerald-400",
  undeliverable: "text-red-600",
  dispatch_rejected: "text-red-600",
  returned: "text-purple-700 dark:text-purple-400",
  decided_agreement: "text-emerald-700 dark:text-emerald-400",
  decided_refusal: "text-red-600",
  decided_cancel: "text-[var(--color-text-muted)]",
  decided_outdated: "text-amber-700 dark:text-amber-400",
  cancelled: "text-[var(--color-text-muted)]",
};

const PENDING_STATES: WpwMailState[] = ["ready", "scheduled", "held", "blocked"];
// Wysłany list jeszcze bez wyniku — koordynator może go w każdej chwili zamknąć jako nieaktualny.
const OPEN_SENT_STATES: WpwMailState[] = ["dispatched", "sent", "returned"];

/**
 * „Opcje dla zaznaczonych”: te same warunki, przy których wiersz listu pokazuje dany przycisk.
 * Operacja obejmuje tylko listy, których dotyczy; resztę zaznaczonych wymienia jako pominięte
 * (z powodem) — backend i tak sprawdza każdy list osobno.
 */
const BULK_OPS: { value: WpwMailBulkAction; label: string; skip: (l: WpwMailLetter) => string | null }[] = [
  { value: "send_now", label: "Wyślij teraz",
    skip: (l) => (l.effective_blocked ? "blokada" : l.is_stale ? "pakiet nieaktualny"
      : l.send_blocker ? "poprzedni list bez wyniku" : null) },
  { value: "hold", label: "Wstrzymaj",
    skip: (l) => (l.effective_blocked ? "blokada" : l.state === "held" ? "już wstrzymany" : null) },
  { value: "resume", label: "Wznów",
    skip: (l) => (l.effective_blocked ? "blokada" : l.state !== "held" ? "nie jest wstrzymany" : null) },
  { value: "cancel", label: "Anuluj wysyłkę", skip: () => null },
];

function lettersCount(n: number): string {
  const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return `${n} ${n === 1 ? "list" : few ? "listy" : "listów"}`;
}

const isOverdue = (l: WpwMailLetter) => !!l.due_at && new Date(l.due_at).getTime() <= Date.now();

function apiMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { errors?: { detail?: string | string[] } } } })?.response?.data?.errors?.detail;
  if (Array.isArray(detail)) return detail[0] ?? fallback;
  return detail || fallback;
}

/** Przełącznik blokady z wymaganym powodem — wspólny dla działki i właściciela. */
function BlockToggle({
  blocked,
  label,
  unblockLabel,
  hint,
  pending,
  onSet,
  testId,
}: {
  blocked: boolean;
  label: string;
  unblockLabel: string;
  hint: string;
  pending: boolean;
  onSet: (blocked: boolean, reason: string) => void;
  testId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  if (blocked) {
    return (
      <Button size="sm" variant="ghost" disabled={pending} onClick={() => onSet(false, "")} data-testid={`${testId}-unblock`}>
        <Unlock size={12} /> {unblockLabel}
      </Button>
    );
  }
  if (!editing) {
    return (
      <Button size="sm" variant="ghost" disabled={pending} title={hint} onClick={() => setEditing(true)} data-testid={`${testId}-block`}>
        <Ban size={12} /> {label}
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input
        className="input-base text-[11px] min-w-[12rem]"
        placeholder="Powód (wymagany)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid={`${testId}-reason`}
      />
      <Button size="sm" disabled={!reason.trim() || pending} onClick={() => { onSet(true, reason.trim()); setEditing(false); setReason(""); }}>
        Zablokuj
      </Button>
      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setReason(""); }}>Anuluj</Button>
    </div>
  );
}

/** „Dane ogólne” działki: blokada automatycznej wysyłki listów WPW dla WSZYSTKICH właścicieli. */
export function ParcelMailBlockControl({ parcel, canSubmit }: { parcel: Parcel; canSubmit: boolean }) {
  const m = useWpwMailLetterMutations(parcel.id);
  const [err, setErr] = useState<string | null>(null);
  if (parcel.access_mode !== "consent") return null;
  return (
    <div className="flex flex-col gap-1" data-testid="parcel-mail-block-control">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Mail size={12} className="shrink-0 text-[var(--color-text-muted)]" />
        <span className="font-medium">Listy WPW automatycznie</span>
        <span className={clsx("text-[9px] font-mono uppercase tracking-wide px-1 py-px border",
          parcel.wpw_mail_blocked
            ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300")}>
          {parcel.wpw_mail_blocked ? "zablokowane" : "włączone"}
        </span>
        {canSubmit && (
          <BlockToggle
            blocked={!!parcel.wpw_mail_blocked}
            label="Nie wysyłaj automatycznie (cała działka)"
            unblockLabel="Zdejmij blokadę działki"
            hint="Żaden właściciel tej działki nie dostanie listu z automatu; wysyłka ręczna dalej możliwa."
            pending={m.blockParcel.isPending}
            testId="parcel-mail"
            onSet={(blocked, reason) =>
              m.blockParcel.mutate({ parcelId: parcel.id, blocked, reason }, { onError: (e) => setErr(apiMessage(e, "Nie udało się zmienić blokady.")) })}
          />
        )}
      </div>
      <span className="pl-6 text-[11px] text-[var(--color-text-muted)]">
        {parcel.wpw_mail_blocked
          ? `Zablokowano${parcel.wpw_mail_blocked_by_name ? ` — ${parcel.wpw_mail_blocked_by_name}` : ""}${parcel.wpw_mail_blocked_at ? `, ${fmtDate(parcel.wpw_mail_blocked_at)}` : ""}${parcel.wpw_mail_blocked_reason ? `: ${parcel.wpw_mail_blocked_reason}` : ""}. Listy czekają; wysyłka ręczna dalej możliwa.`
          : "Po progu dni od przyjęcia zlecenia WPW przez podwykonawcę system sam zleca wysyłkę pakietu listownego każdemu właścicielowi bez zgody. Blokada per właściciel jest na jego karcie."}
      </span>
      {err && <p className="pl-6 text-[11px] text-red-600">{err}</p>}
    </div>
  );
}

/** Karta właściciela prywatnego: trwała blokada automatycznej wysyłki listu TEMU właścicielowi. */
export function OwnerMailBlockControl({ owner, canSubmit = true }: { owner: ParcelOwner; canSubmit?: boolean }) {
  const m = useWpwMailLetterMutations(owner.parcel);
  const [err, setErr] = useState<string | null>(null);
  const letter = owner.wpw_mail_letter;
  return (
    <div className="flex flex-col gap-1 border-t border-[var(--color-border-muted)] pt-2" data-testid="owner-mail-block-control">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <Mail size={12} className="shrink-0 text-[var(--color-text-muted)]" />
        <span className="font-medium">List WPW automatycznie:</span>
        {owner.wpw_mail_blocked ? (
          <span className="text-amber-700 dark:text-amber-400">
            zablokowany{owner.wpw_mail_blocked_by_name ? ` — ${owner.wpw_mail_blocked_by_name}` : ""}
            {owner.wpw_mail_blocked_at ? `, ${fmtDate(owner.wpw_mail_blocked_at)}` : ""}
            {owner.wpw_mail_blocked_reason ? `: ${owner.wpw_mail_blocked_reason}` : ""}
          </span>
        ) : letter ? (
          <span className={STATE_TONE[letter.state]}>
            {letter.state_display}{letter.due_at && PENDING_STATES.includes(letter.state) ? ` · termin ${fmtDate(letter.due_at)}` : ""}
          </span>
        ) : (
          <span className="text-[var(--color-text-muted)]">brak pakietu listownego</span>
        )}
        {canSubmit && letter && !owner.wpw_mail_blocked && !letter.blocked && ["ready", "scheduled", "held"].includes(letter.state) && (
          <Button size="sm" variant="secondary" disabled={m.sendNow.isPending || letter.is_stale || !!letter.send_blocker}
                  title={letter.is_stale ? "Pakiet nieaktualny — najpierw skompiluj pakiety"
                    : letter.send_blocker || "Pomiń czas oczekiwania: zleć wydruk z wysyłką od razu"}
                  onClick={() => m.sendNow.mutate({ id: letter.id }, { onError: (e) => setErr(apiMessage(e, "Nie udało się zlecić wysyłki.")) })}
                  data-testid="owner-mail-send-now">
            <Send size={12} /> Wyślij teraz
          </Button>
        )}
        {canSubmit && (
          <BlockToggle
            blocked={!!owner.wpw_mail_blocked}
            label="Nie wysyłaj automatycznie"
            unblockLabel="Zdejmij blokadę"
            hint="Ten właściciel nie dostanie listu z automatu (trwale, także przy kolejnych zleceniach); wysyłka ręczna dalej możliwa."
            pending={m.blockOwner.isPending}
            testId="owner-mail"
            onSet={(blocked, reason) =>
              m.blockOwner.mutate({ ownerId: owner.id, blocked, reason }, { onError: (e) => setErr(apiMessage(e, "Nie udało się zmienić blokady.")) })}
          />
        )}
      </div>
      {err && <p className="text-[11px] text-red-600">{err}</p>}
    </div>
  );
}

function LetterRow({
  letter,
  parcelId,
  onError,
  selected = false,
  onToggleSelect,
}: {
  letter: WpwMailLetter;
  parcelId: string;
  onError: (msg: string | null) => void;
  selected?: boolean;
  /** Checkbox do „Opcji dla zaznaczonych” — tylko listy czekające, którymi można zarządzać. */
  onToggleSelect?: () => void;
}) {
  const m = useWpwMailLetterMutations(parcelId);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [decideNote, setDecideNote] = useState("");
  const [markingOutdated, setMarkingOutdated] = useState(false);
  const [outdatedNote, setOutdatedNote] = useState("");
  const pending = PENDING_STATES.includes(letter.state);
  const canMarkOutdated = letter.can_decide && !letter.decision && OPEN_SENT_STATES.includes(letter.state);
  const overdue = isOverdue(letter);
  const busy = m.cancel.isPending || m.hold.isPending || m.resume.isPending || m.sendNow.isPending || m.decide.isPending
    || m.markOutdated.isPending;
  const run = <T,>(mutation: { mutate: (v: T, o: { onError: (e: unknown) => void }) => void }, v: T, fallback: string) =>
    mutation.mutate(v, { onError: (e) => onError(apiMessage(e, fallback)) });

  return (
    <li className="border border-[var(--color-border-muted)] p-2 space-y-1.5 text-[11px] font-mono" data-testid="wpw-mail-letter">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[var(--color-text-primary)]">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              aria-label={`Zaznacz list: ${letter.owner?.full_name ?? "właściciel"}`}
              data-testid="wpw-mail-select"
            />
          )}
          <Mail size={12} className="shrink-0" />
          {letter.owner?.full_name ?? "właściciel"}
          <span className="text-[var(--color-text-muted)]">· {letter.application.number}</span>
          {letter.previous_letter && (
            <span className="text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border-muted)] text-[var(--color-text-muted)]"
                  title="Pakiet miał już list zlecony do wysyłki — ten powstał przy kolejnej kompilacji, na wypadek odmowy i ponownej wysyłki">
              list kolejny
            </span>
          )}
        </span>
        <span className={clsx("font-medium", STATE_TONE[letter.state])} title={letter.effective_blocked_reason || letter.hold_reason_display || undefined}>
          {letter.state_display}
          {letter.print_order && ` · zlecenie #${letter.print_order.number}`}
          {letter.print_order?.tracking_number && ` · nr listu ${letter.print_order.tracking_number}`}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[var(--color-text-muted)]">
        {/* Do pobrania tylko kompletny pakiet (pismo + 2 komplety); samo pismo przewodnie
            nie wychodzi osobno. */}
        {letter.file && (
          <ManagedFileLink file={letter.file}>pakiet listowny</ManagedFileLink>
        )}
        {letter.compiled_at && <span>złożono {fmtDate(letter.compiled_at)}</span>}
        {letter.due_at && pending && (
          <span className={clsx(overdue && "text-amber-700 dark:text-amber-400")}>
            termin wysyłki {fmtDate(letter.due_at)}{overdue ? " (minął)" : ""}
          </span>
        )}
        {!letter.due_at && letter.state === "ready" && !letter.clock_note && (
          <span>czeka na przyjęcie zamówienia z sekwencji masowej przez podwykonawcę</span>
        )}
        {pending && letter.clock_note && <span data-testid="wpw-mail-clock-note">{letter.clock_note}</span>}
        {letter.dispatched_at && <span>zlecono {fmtDateTime(letter.dispatched_at)}</span>}
        {letter.dispatched_at && letter.route_version_label && (
          <span title="Wersja trasy obowiązująca w chwili wysyłki — z nią porównuje się przebieg na działce po wgraniu nowej">
            trasa przy wysyłce: {letter.route_version_label}
          </span>
        )}
        {letter.print_order && (
          letter.proceeding_item
            ? <span>podpięty pod właściciela (Listy do właścicieli)</span>
            : letter.detached
              ? <span className="text-amber-700 dark:text-amber-400">odpięty od właściciela</span>
              : null
        )}
        {letter.returned_at && <span>zwrot {fmtDate(letter.returned_at)}</span>}
      </div>

      {letter.previous_letter && (
        <p className="text-[var(--color-text-muted)]" data-testid="wpw-mail-previous">
          poprzedni list: {letter.previous_letter.state_display}
          {letter.previous_letter.print_order_number != null && ` · zlecenie #${letter.previous_letter.print_order_number}`}
          {letter.previous_letter.outdated && " · może być nieaktualny (trasa zmieniła się po wysyłce)"}
        </p>
      )}
      {pending && letter.send_blocker && (
        <p className="text-amber-700 dark:text-amber-400" data-testid="wpw-mail-send-blocker">{letter.send_blocker}</p>
      )}
      {letter.is_stale && pending && (
        <p className="text-amber-700 dark:text-amber-400">⚠ nieaktualny — {letter.stale_reason}</p>
      )}
      {/* „Oznacz jako nieaktualny” (koordynator) — przy każdym wysłanym liście bez wyniku, nie tylko
          po wykrytej zmianie trasy (listy sprzed sprawdzania, mapa wgrana ręcznie, inny powód).
          Zamyka list jak odmowa (tytuł właściciela bez zmian) — list kolejny można wtedy wysłać.
          Wykryta zmiana trasy dokłada ostrzeżenie z powodem i wyróżnia przycisk. */}
      {(letter.outdated || canMarkOutdated) && (
        <div className={clsx("flex flex-wrap items-center gap-2", letter.outdated && "text-amber-700 dark:text-amber-400")}>
          {letter.outdated && (
            <span data-testid="wpw-mail-outdated">⚠ list może być nieaktualny — {letter.outdated_reason || "trasa zmieniła się na działce po wysyłce"}</span>
          )}
          {canMarkOutdated && !markingOutdated && (
            <Button size="sm" variant={letter.outdated ? "warning" : "ghost"} disabled={busy} onClick={() => setMarkingOutdated(true)}
                    title={(letter.outdated ? "" : "Np. po wysyłce zmieniła się mapa albo treść pakietu. ")
                      + "List liczy się tak, jakby nie został wysłany: odpina się od właściciela (bez odmowy, tytuł bez zmian), "
                      + "kolejny list można wysłać ręcznie, a nowe zamówienie WPW uruchomi automat"}
                    data-testid="wpw-mail-mark-outdated">
              <AlertTriangle size={12} /> Oznacz jako nieaktualny
            </Button>
          )}
          {canMarkOutdated && markingOutdated && (
            <div className="flex flex-wrap items-center gap-1.5 w-full">
              <input
                className="input-base text-[11px] flex-1 min-w-[12rem]"
                placeholder="Uwagi (opcjonalnie)"
                value={outdatedNote}
                onChange={(e) => setOutdatedNote(e.target.value)}
                data-testid="wpw-mail-outdated-note"
              />
              <Button size="sm" variant="warning" disabled={busy}
                      onClick={() => {
                        run(m.markOutdated, { id: letter.id, note: outdatedNote.trim() }, "Nie udało się oznaczyć listu.");
                        setMarkingOutdated(false);
                      }}
                      data-testid="wpw-mail-mark-outdated-confirm">
                Oznacz jako nieaktualny
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setMarkingOutdated(false); setOutdatedNote(""); }}>Wróć</Button>
            </div>
          )}
        </div>
      )}
      {letter.state === "held" && (
        <p className="text-amber-700 dark:text-amber-400">wstrzymano: {letter.hold_reason_display}{letter.held_at ? ` (${fmtDate(letter.held_at)})` : ""}</p>
      )}
      {letter.effective_blocked && (
        <p className="text-amber-700 dark:text-amber-400">
          {letter.parcel_blocked
            ? `blokada całej działki (Dane ogólne)${letter.parcel_blocked_reason ? `: ${letter.parcel_blocked_reason}` : ""}`
            : `blokada właściciela (karta właściciela)${letter.owner_blocked_reason ? `: ${letter.owner_blocked_reason}` : ""}`}
        </p>
      )}
      {letter.state === "cancelled" && (
        <p>
          powód: {letter.cancel_reason_display}
          {letter.cancelled_by_name && ` — ${letter.cancelled_by_name}`}
          {letter.cancel_note && ` · ${letter.cancel_note}`}
          {letter.cancel_reason === "manual" && " · nowe zlecenie WPW uzbroi list ponownie"}
        </p>
      )}
      {letter.decision && (
        <p>
          wynik: {letter.decision_display}
          {letter.decided_by_name && ` — ${letter.decided_by_name}`}
          {letter.decided_at && `, ${fmtDate(letter.decided_at)}`}
          {letter.decision_note && ` · ${letter.decision_note}`}
        </p>
      )}
      {letter.return_entry && letter.return_entry.files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span>skan zwrotu:</span>
          {letter.return_entry.files.map((f) => (
            <ManagedFileLink key={f.id} file={f}>{f.original_filename}</ManagedFileLink>
          ))}
        </div>
      )}

      {/* ── akcje: anuluj (jednorazowo) / wznów / wyślij — gdy list czeka ── */}
      {letter.can_manage && pending && (
        <div className="flex flex-wrap items-center gap-2">
          {cancelling ? (
            <div className="flex flex-wrap items-center gap-1.5 w-full">
              <input
                className="input-base text-[11px] flex-1 min-w-[12rem]"
                placeholder="Powód (opcjonalnie)"
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
              />
              <Button size="sm" variant="warning" disabled={busy}
                      onClick={() => { run(m.cancel, { id: letter.id, note: cancelNote.trim() }, "Nie udało się anulować."); setCancelling(false); }}>
                Anuluj wysyłkę
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setCancelling(false); setCancelNote(""); }}>Wróć</Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setCancelling(true)}
                    title="Jednorazowo: ten list nie wyjdzie w tym zleceniu WPW; nowe zlecenie uzbroi go ponownie. Blokada trwała: Dane ogólne (działka) albo karta właściciela.">
              <XCircle size={12} /> Anuluj wysyłkę
            </Button>
          )}
          {/* „Wstrzymaj" (ręcznie, do odwołania) i „Wznów" — także dla listu wstrzymanego
              automatycznie po wgraniu trasy (mimo nieaktualności, to decyzja człowieka). */}
          {!letter.effective_blocked && (letter.state === "ready" || letter.state === "scheduled") && (
            <Button size="sm" variant="ghost" disabled={busy}
                    onClick={() => run(m.hold, { id: letter.id }, "Nie udało się wstrzymać.")}
                    title="Wstrzymaj wysyłkę do odwołania — odliczanie biegnie, ale list nie wyjdzie, dopóki nie klikniesz „Wznów”"
                    data-testid="wpw-mail-hold">
              <Pause size={12} /> Wstrzymaj
            </Button>
          )}
          {!letter.effective_blocked && letter.state === "held" && (
            <Button size="sm" variant="secondary" disabled={busy}
                    onClick={() => run(m.resume, { id: letter.id }, "Nie udało się wznowić.")}
                    title={overdue
                      ? "Termin minął — wznowienie od razu zleca wysyłkę"
                      : "Wznów odliczanie — list wyjdzie w terminie" + (letter.is_stale ? " (mimo nieaktualności pakietu)" : "")}
                    data-testid="wpw-mail-resume">
              <Play size={12} /> {overdue ? "Wznów i wyślij" : "Wznów"}
            </Button>
          )}
          {/* „Wyślij teraz” pomija odliczanie (także bez przyjętego zamówienia) i zdejmuje
              wstrzymanie — zlecenie wydruku z wysyłką powstaje od razu. */}
          {!letter.effective_blocked && (
            <Button size="sm" variant="secondary" disabled={busy || letter.is_stale || !!letter.send_blocker}
                    onClick={() => run(m.sendNow, { id: letter.id }, "Nie udało się zlecić wysyłki.")}
                    title={letter.is_stale ? "Pakiet nieaktualny — najpierw skompiluj pakiety"
                      : letter.send_blocker || "Pomiń czas oczekiwania: zleć wydruk z wysyłką od razu"}
                    data-testid="wpw-mail-send-now">
              <Send size={12} /> Wyślij teraz
            </Button>
          )}
        </div>
      )}

      {/* ── zwrot: decyzja koordynatora ── */}
      {letter.state === "returned" && letter.can_decide && (
        <div className="space-y-1.5 border-t border-[var(--color-border-muted)] pt-1.5">
          <span className="section-label">Wrócił list — co jest w środku?</span>
          <input
            className="input-base text-[11px] w-full"
            placeholder="Uwagi (opcjonalnie)"
            value={decideNote}
            onChange={(e) => setDecideNote(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy}
                    onClick={() => run(m.decide, { id: letter.id, outcome: "agreement", note: decideNote }, "Nie udało się zapisać wyniku.")}>
              Zgoda (uzgodnienie)
            </Button>
            <Button size="sm" variant="secondary" disabled={busy}
                    onClick={() => run(m.decide, { id: letter.id, outcome: "refusal", note: decideNote }, "Nie udało się zapisać wyniku.")}>
              Odmowa
            </Button>
            <Button size="sm" variant="ghost" disabled={busy}
                    title="Skan nie podpina się nigdzie — list zostaje na liście"
                    onClick={() => run(m.decide, { id: letter.id, outcome: "cancel_attach", note: decideNote }, "Nie udało się zapisać wyniku.")}>
              Anuluj podpinanie
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

type BulkResult = { ok: string; failed: string[] };

/** Pasek nad listami czekającymi: „zaznacz wszystkie” + rozwijane „Opcje dla zaznaczonych”
 * (wyślij teraz / wstrzymaj / wznów / anuluj wysyłkę) z potwierdzeniem. Podsumowanie trzyma
 * panel — po „Wyślij teraz” kolejka bywa pusta i pasek znika razem z nią. */
function BulkBar({
  parcelId,
  selectable,
  selected,
  setSelected,
  onResult,
  onError,
}: {
  parcelId: string;
  selectable: WpwMailLetter[];
  selected: WpwMailLetter[];
  setSelected: (ids: Set<string>) => void;
  onResult: (r: BulkResult | null) => void;
  onError: (msg: string | null) => void;
}) {
  const m = useWpwMailLetterMutations(parcelId);
  const [op, setOp] = useState<WpwMailBulkAction | "">("");
  const [note, setNote] = useState("");
  const allRef = useRef<HTMLInputElement>(null);
  const all = selectable.length > 0 && selected.length === selectable.length;
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = selected.length > 0 && !all;
  }, [selected.length, all]);

  const chosen = BULK_OPS.find((o) => o.value === op);
  const applicable = chosen ? selected.filter((l) => !chosen.skip(l)) : [];
  const skipped = chosen ? selected.filter((l) => chosen.skip(l)) : [];
  const close = () => { setOp(""); setNote(""); };

  const run = () => {
    if (!chosen || applicable.length === 0) return;
    onError(null);
    onResult(null);
    m.bulk.mutate(
      { ids: applicable.map((l) => l.id), action: chosen.value, note: note.trim() },
      {
        onSuccess: (res) => {
          onResult({
            ok: `${chosen.label}: wykonano dla ${lettersCount(res.done.length)}.`,
            failed: res.failed.map((f) => `${f.owner ?? "list"} — ${f.error}`),
          });
          setSelected(new Set());
          close();
        },
        onError: (e) => onError(apiMessage(e, "Nie udało się wykonać operacji na zaznaczonych listach.")),
      },
    );
  };

  return (
    // Cała sekcja (zaznaczanie + opcje + potwierdzenie) odcina się od listy listów kolorem;
    // po wybraniu anulowania przechodzi w ton ostrzeżenia, jak jego przycisk.
    <div
      className={clsx(
        "space-y-1.5 border border-l-4 p-2",
        chosen?.value === "cancel"
          ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40"
          : "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/40",
      )}
      data-testid="wpw-mail-bulk"
    >
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            ref={allRef}
            type="checkbox"
            checked={all}
            onChange={() => setSelected(new Set(all ? [] : selectable.map((l) => l.id)))}
            data-testid="wpw-mail-select-all"
          />
          zaznacz wszystkie
        </label>
        <span className="text-[var(--color-text-muted)]">{selected.length} zaznaczonych</span>
        <select
          className="input-base text-[11px] py-0.5 w-auto cursor-pointer"
          aria-label="Opcje dla zaznaczonych"
          value={op}
          disabled={selected.length === 0 || m.bulk.isPending}
          onChange={(e) => { setOp(e.target.value as WpwMailBulkAction | ""); onResult(null); onError(null); }}
          data-testid="wpw-mail-bulk-select"
        >
          <option value="">Opcje dla zaznaczonych…</option>
          {BULK_OPS.map((o) => {
            const n = selected.filter((l) => !o.skip(l)).length;
            return (
              <option key={o.value} value={o.value} disabled={n === 0}>
                {o.label} ({n})
              </option>
            );
          })}
        </select>
      </div>

      {chosen && (
        <div
          className={clsx(
            "space-y-1.5 border-t pt-1.5 text-[11px] font-mono",
            chosen.value === "cancel" ? "border-amber-300 dark:border-amber-700" : "border-blue-300 dark:border-blue-700",
          )}
          data-testid="wpw-mail-bulk-confirm"
        >
          <p>
            {chosen.label}: {lettersCount(applicable.length)}.
            {chosen.value === "send_now" && " Zlecenie wydruku z wysyłką powstanie od razu dla każdego z nich."}
            {chosen.value === "resume" && applicable.some(isOverdue) && " Listy po terminie wyjdą od razu."}
            {chosen.value === "cancel" && " Jednorazowo — nowe zlecenie WPW uzbroi je ponownie."}
          </p>
          {skipped.length > 0 && (
            <p className="text-[var(--color-text-muted)]" data-testid="wpw-mail-bulk-skipped">
              Pominięte: {skipped.map((l) => `${l.owner?.full_name ?? "właściciel"} (${chosen.skip(l)})`).join(", ")}
            </p>
          )}
          {(chosen.value === "cancel" || chosen.value === "hold") && (
            <input
              className="input-base text-[11px] w-full"
              placeholder="Powód (opcjonalnie)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="wpw-mail-bulk-note"
            />
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={chosen.value === "cancel" ? "warning" : "secondary"}
                    disabled={applicable.length === 0 || m.bulk.isPending} onClick={run}
                    data-testid="wpw-mail-bulk-run">
              {chosen.label} ({applicable.length})
            </Button>
            <Button size="sm" variant="ghost" onClick={close}>Wróć</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function WpwMailLettersPanel({ parcel }: { parcel: Parcel; canSubmit?: boolean }) {
  const { data: letters, isLoading } = useWpwMailLetters({ parcel: parcel.id });
  const [err, setErr] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  if (isLoading) return <Spinner size="sm" />;
  const rows = letters ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-[11px] font-mono text-[var(--color-text-muted)]" data-testid="wpw-mail-empty">
        Brak pakietów listownych — powstają przy kompilacji pakietów WPW masowych (tylko ENERGA).
      </p>
    );
  }
  const waiting = rows.filter((l) => PENDING_STATES.includes(l.state));
  const rest = rows.filter((l) => !PENDING_STATES.includes(l.state));
  // Zaznaczać da się tylko listy czekające, którymi użytkownik zarządza; zaznaczenie listu,
  // który w międzyczasie wyszedł z kolejki, po prostu znika.
  const selectable = waiting.filter((l) => l.can_manage);
  const selected = selectable.filter((l) => selectedIds.has(l.id));
  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  return (
    <div className="space-y-2" data-testid="wpw-mail-panel">
      <span className="section-label">Ścieżka listowna — listy automatyczne</span>
      {parcel.wpw_mail_blocked && (
        <p className="text-[11px] font-mono text-amber-700 dark:text-amber-400">
          Blokada całej działki (Dane ogólne){parcel.wpw_mail_blocked_reason ? `: ${parcel.wpw_mail_blocked_reason}` : ""}. Listy czekają; wysyłka ręczna dalej możliwa.
        </p>
      )}
      {waiting.length > 0 && (
        <>
          <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">Czekają na wysyłkę ({waiting.length})</span>
          {selectable.length > 1 && (
            <BulkBar parcelId={parcel.id} selectable={selectable} selected={selected} setSelected={setSelectedIds}
                     onResult={setBulkResult} onError={setErr} />
          )}
          <ul className="space-y-1.5">
            {waiting.map((l) => (
              <LetterRow
                key={l.id}
                letter={l}
                parcelId={parcel.id}
                onError={setErr}
                selected={selectedIds.has(l.id)}
                onToggleSelect={selectable.length > 1 && l.can_manage ? () => toggle(l.id) : undefined}
              />
            ))}
          </ul>
        </>
      )}
      {rest.length > 0 && (
        <>
          <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">Wysłane i zakończone ({rest.length})</span>
          <ul className="space-y-1.5">
            {rest.map((l) => <LetterRow key={l.id} letter={l} parcelId={parcel.id} onError={setErr} />)}
          </ul>
        </>
      )}
      {bulkResult && (
        <div className="text-[11px] font-mono" data-testid="wpw-mail-bulk-result">
          <p className="text-emerald-700 dark:text-emerald-400">{bulkResult.ok}</p>
          {bulkResult.failed.length > 0 && (
            <ul className="text-red-600">
              {bulkResult.failed.map((f) => <li key={f}>✗ {f}</li>)}
            </ul>
          )}
        </div>
      )}
      {err && <p className="text-[11px] text-red-600">{err}</p>}
      <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
        Pakiet listowny (pismo przewodnie + dwa komplety dokumentów, duplex) powstaje razem z pakietem terenowym. Zegar
        rusza, gdy podwykonawca przyjmie zamówienie z sekwencji masowej; po upływie progu dni bez zgody system sam
        zleca wydruk z wysyłką w Dzienniku, a wysłany list podpina pod właściciela. Zgłoszenie z terenu wstrzymuje
        (zebrano / zgoda bez akceptacji / odrzucona) albo anuluje (odmowa, zgon, tytuł pozyskany, list ręczny).
        „Anuluj wysyłkę” działa na to zlecenie; blokada trwała jest w Danych ogólnych (działka) i na karcie właściciela.
        Zwrot rozpoznany po kodzie QR pisma ląduje tutaj do rozstrzygnięcia. Wysłany list się nie zmienia — kolejna
        kompilacja składa list kolejny, który po odmowie wysyłasz ręcznie. List oznaczony jako nieaktualny liczy się
        tak, jakby nie został wysłany: znika z listów do właściciela (bez odmowy), a nowe zamówienie WPW uruchamia
        automat. List pamięta trasę z chwili wysyłki — gdy nowa trasa zmieni przebieg na działce, pojawi się
        ostrzeżenie, że może być nieaktualny.
      </p>
    </div>
  );
}
